import React from 'react';
import Layout from '../components/layout/Layout';
import { useAuthStore } from '../stores/authStore';
import { useUserData } from '../hooks/useUserData';

// Component imports for modular structure
import CreditUsageSection from '../components/account/CreditUsageSection';
import ApiKeySection from '../components/account/ApiKeySection';
import RecentCreditHistorySection from '../components/account/RecentCreditHistorySection';
import AccountPreferencesSection from '../components/account/AccountPreferencesSection';
import TransactionHistorySection from '../components/account/TransactionHistorySection';
import MembershipInfo from '../components/account/MembershipInfo';

const AccountSettingsPage: React.FC = () => {
  const { user } = useAuthStore();
  // Use the REUSABLE hook for ALL user data - SAME PATTERN as useSimpleCredit
  const { userInfo } = useUserData();

  return (
    <Layout>
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
        <div className="container mx-auto px-3 sm:px-4 py-6 sm:py-8 max-w-7xl">
          {/* Page Header with Breadcrumbs */}
          <div className="mb-4 sm:mb-8">
            <nav className="flex items-center text-sm text-gray-600 mb-4" aria-label="Breadcrumb">
              <a href="/" className="hover:text-blue-600 transition-colors">Home</a>
              <svg className="mx-2 w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
              </svg>
              <span className="text-blue-600 font-medium">Account Settings</span>
            </nav>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h1 className="text-3xl font-bold text-gray-900 mb-2">Account Settings</h1>
                <MembershipInfo 
                  currentPackage={userInfo?.current_package}
                  membershipExpiresAt={userInfo?.membership_expires_at}
                  freeAccountStatus={userInfo?.free_account_status}
                  subscriptionStatus={userInfo?.subscription_status}
                />
              </div>
            </div>
          </div>

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 xl:grid-cols-5 gap-4 sm:gap-8">
            {/* Left Column - Credit & Usage + API Key + Recent Activity + Transaction History */}
            <div className="xl:col-span-3 space-y-4 sm:space-y-8">
              {/* Credit & Usage Section */}
              <CreditUsageSection />
              
              {/* API Key Section */}
              <ApiKeySection />
              
              {/* Recent Credit History Section */}
              <RecentCreditHistorySection />
              
              {/* Transaction History Section */}
              <TransactionHistorySection limit={5} />
            </div>

            {/* Right Sidebar - Account Preferences */}
            <div className="xl:col-span-2">
              <AccountPreferencesSection user={user} />
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default AccountSettingsPage;
